import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type MouseEvent, type ReactNode } from "react";
import { ChevronDown, ChevronRight, Folder, FolderPlus, Play, Square, Trash2 } from "lucide-react";
import type { SampleAssetMeta, SampleFolderMeta } from "../../../../../src/lib/audio";
import { drumKitSampleIds, samplerPresetSampleIds } from "../../../../../src/lib/audio";
import { useStoreSelector } from "../../../../../src/lib/store";
import { getLabActions } from "../../labActions";
import {
  clearSampleClipboard,
  getSampleClipboard,
  hasSampleClipboard,
  sampleClipboardIsCut,
  setSampleClipboard,
} from "../../lib/sampleClipboard";
import {
  isFolderAncestor,
  listImportedSampleAssets,
  listSampleFolders,
  resolveSampleRuntime,
  subscribeSampleCatalog,
} from "../../lib/sampleLibrary";
import {
  audioFilesFromDrop,
  folderIdFromDrop,
  isAudioFileDrag,
  isLibraryFolderDrag,
  isLibrarySampleDrag,
  sampleIdFromDrop,
  setFolderDragData,
  setSampleDragData,
} from "../../lib/sampleDrop";
import { startSamplePreview, type SamplePreviewHandle } from "../../lib/samplePreview";
import { projectStore } from "../../store/projectStore";
import { patchUi, uiStore } from "../../store/uiStore";
import {
  SampleLibraryContextMenu,
  type SampleLibraryContextTarget,
  type SampleLibraryMenuAction,
} from "./SampleLibraryContextMenu";
import {
  dockPanel,
  dockPanelClose,
  dockPanelHeader,
  dockPanelScroll,
  dockPanelTitle,
} from "./dockChrome";

type SampleUsage = {
  tracks: number[];
  bankNames: string[];
};

type DropTarget = "root" | string;

type ContextState = {
  target: SampleLibraryContextTarget;
  point: { x: number; y: number };
};

function normalizeParent(id: string | null | undefined): string | null {
  return id == null || id === "" ? null : id;
}

function useSampleUsageMap(): Map<string, SampleUsage> {
  const instruments = useStoreSelector(projectStore, s => s.instruments);
  const samplerBank = useStoreSelector(projectStore, s => s.samplerBank);
  const drumBank = useStoreSelector(projectStore, s => s.drumBank);

  return useMemo(() => {
    const map = new Map<string, SampleUsage>();
    const touch = (id: string): SampleUsage => {
      let entry = map.get(id);
      if (!entry) {
        entry = { tracks: [], bankNames: [] };
        map.set(id, entry);
      }
      return entry;
    };
    instruments.forEach((inst, slot) => {
      if (inst.kind === "sampler") {
        for (const id of samplerPresetSampleIds(inst.preset)) {
          const entry = touch(id);
          if (!entry.tracks.includes(slot)) {
            entry.tracks.push(slot);
          }
        }
      } else if (inst.kind === "drums") {
        for (const id of drumKitSampleIds(inst.kit)) {
          const entry = touch(id);
          if (!entry.tracks.includes(slot)) {
            entry.tracks.push(slot);
          }
        }
      }
    });
    for (const entry of samplerBank) {
      for (const id of samplerPresetSampleIds(entry.preset)) {
        const use = touch(id);
        if (!use.bankNames.includes(entry.name)) {
          use.bankNames.push(entry.name);
        }
      }
    }
    for (const entry of drumBank) {
      for (const id of drumKitSampleIds(entry.kit)) {
        const use = touch(id);
        if (!use.bankNames.includes(entry.name)) {
          use.bankNames.push(entry.name);
        }
      }
    }
    return map;
  }, [instruments, samplerBank, drumBank]);
}

function compareSortThenName(
  a: { name: string; id: string; sortKey?: number },
  b: { name: string; id: string; sortKey?: number },
): number {
  const sa = a.sortKey ?? 0;
  const sb = b.sortKey ?? 0;
  if (sa !== sb) {
    return sa - sb;
  }
  return a.name.localeCompare(b.name) || a.id.localeCompare(b.id);
}

/**
 * Host sample library browser — folders / search / rename / delete / DnD.
 * Stacked under Arrange in the right rail (RightSidebar).
 */
export function SampleManagerDock() {
  const open = useStoreSelector(uiStore, s => s.samplesOpen);
  const panelRef = useRef<HTMLDivElement>(null);
  const [assets, setAssets] = useState<SampleAssetMeta[]>([]);
  const [folders, setFolders] = useState<SampleFolderMeta[]>([]);
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [auditionBusy, setAuditionBusy] = useState(false);
  const [fileDragOver, setFileDragOver] = useState(false);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [libraryBusy, setLibraryBusy] = useState(false);
  const [wipeConfirmOpen, setWipeConfirmOpen] = useState(false);
  const [deleteFolderConfirmId, setDeleteFolderConfirmId] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextState | null>(null);
  const [clipboardEpoch, setClipboardEpoch] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);
  const auditionRef = useRef<SamplePreviewHandle | null>(null);
  const auditionRafRef = useRef<number | null>(null);
  const usage = useSampleUsageMap();

  const reload = useCallback(() => {
    void Promise.all([listImportedSampleAssets(), listSampleFolders()]).then(
      ([nextAssets, nextFolders]) => {
        setAssets(nextAssets);
        setFolders(nextFolders);
      },
    );
  }, []);

  const stopAudition = useCallback(() => {
    if (auditionRafRef.current != null) {
      cancelAnimationFrame(auditionRafRef.current);
      auditionRafRef.current = null;
    }
    auditionRef.current?.stop();
    auditionRef.current = null;
    setPlayingId(null);
  }, []);

  useEffect(() => subscribeSampleCatalog(reload), [reload]);
  useEffect(() => {
    if (open) {
      reload();
      panelRef.current?.focus();
    } else {
      stopAudition();
    }
  }, [open, reload, stopAudition]);

  useEffect(() => () => stopAudition(), [stopAudition]);

  useEffect(() => {
    if (selectedFolderId && !folders.some(f => f.id === selectedFolderId)) {
      setSelectedFolderId(null);
    }
  }, [folders, selectedFolderId]);

  const folderById = useMemo(() => {
    const map = new Map<string, SampleFolderMeta>();
    for (const folder of folders) {
      map.set(folder.id, folder);
    }
    return map;
  }, [folders]);

  const folderPathLabel = useCallback(
    (folderId: string | null | undefined): string => {
      const parts: string[] = [];
      let cursor = normalizeParent(folderId);
      const seen = new Set<string>();
      while (cursor && !seen.has(cursor)) {
        seen.add(cursor);
        const folder = folderById.get(cursor);
        if (!folder) {
          break;
        }
        parts.unshift(folder.name);
        cursor = normalizeParent(folder.parentId);
      }
      return parts.length > 0 ? parts.join(" / ") : "Library";
    },
    [folderById],
  );

  const watchAudition = useCallback((id: string, handle: SamplePreviewHandle) => {
    const tick = (): void => {
      if (!handle.playing()) {
        auditionRafRef.current = null;
        if (auditionRef.current === handle) {
          auditionRef.current = null;
          setPlayingId(prev => (prev === id ? null : prev));
        }
        return;
      }
      auditionRafRef.current = requestAnimationFrame(tick);
    };
    auditionRafRef.current = requestAnimationFrame(tick);
  }, []);

  const toggleAudition = useCallback(async (asset: SampleAssetMeta) => {
    if (playingId === asset.id) {
      stopAudition();
      return;
    }
    stopAudition();
    setAuditionBusy(true);
    try {
      const actions = getLabActions();
      let ctx = actions?.getAudioContext?.();
      if (!ctx) {
        await actions?.enableAudio?.();
        ctx = actions?.getAudioContext?.();
      }
      if (!ctx) {
        actions?.setStatus?.("Audio engine not ready");
        return;
      }
      if (ctx.state === "suspended") {
        await ctx.resume();
      }
      const sample = await resolveSampleRuntime(asset.id);
      if (!sample?.pcm.length) {
        actions?.setStatus?.(`Sample “${asset.name}” is missing`);
        return;
      }
      const handle = startSamplePreview(ctx, {
        pcm: sample.pcm,
        sampleRate: sample.sampleRate,
        startFrame: 0,
        endFrame: sample.pcm.length,
        loop: false,
      });
      auditionRef.current = handle;
      setPlayingId(asset.id);
      watchAudition(asset.id, handle);
    } catch (error) {
      getLabActions()?.setStatus?.(
        `Audition failed: ${error instanceof Error ? error.message : String(error)}`,
      );
      stopAudition();
    } finally {
      setAuditionBusy(false);
    }
  }, [playingId, stopAudition, watchAudition]);

  const queryLower = query.trim().toLowerCase();
  const searching = queryLower.length > 0;

  const matchingAssets = useMemo(() => {
    if (!searching) {
      return assets;
    }
    return assets.filter(
      a =>
        a.name.toLowerCase().includes(queryLower)
        || a.id.toLowerCase().includes(queryLower)
        || folderPathLabel(a.folderId).toLowerCase().includes(queryLower),
    );
  }, [assets, folderPathLabel, queryLower, searching]);

  const matchingFolderIds = useMemo(() => {
    if (!searching) {
      return null as Set<string> | null;
    }
    const ids = new Set<string>();
    for (const folder of folders) {
      if (folder.name.toLowerCase().includes(queryLower)) {
        ids.add(folder.id);
        let cursor = normalizeParent(folder.parentId);
        while (cursor) {
          ids.add(cursor);
          cursor = normalizeParent(folderById.get(cursor)?.parentId);
        }
      }
    }
    for (const asset of matchingAssets) {
      let cursor = normalizeParent(asset.folderId);
      while (cursor) {
        ids.add(cursor);
        cursor = normalizeParent(folderById.get(cursor)?.parentId);
      }
    }
    return ids;
  }, [folderById, folders, matchingAssets, queryLower, searching]);

  function close(): void {
    patchUi({ samplesOpen: false });
    getLabActions()?.persistPanels({ samplesOpen: false });
    getLabActions()?.setStatus("Samples closed.");
  }

  async function commitRename(id: string): Promise<void> {
    const name = draftName.trim();
    setEditingId(null);
    if (!name) {
      return;
    }
    const current = assets.find(a => a.id === id)?.name;
    if (current === name) {
      return;
    }
    setBusyId(id);
    try {
      await getLabActions()?.renameSample?.(id, name);
    } finally {
      setBusyId(null);
    }
  }

  async function commitFolderRename(id: string): Promise<void> {
    const name = draftName.trim();
    setEditingFolderId(null);
    if (!name) {
      return;
    }
    const current = folders.find(f => f.id === id)?.name;
    if (current === name) {
      return;
    }
    setBusyId(id);
    try {
      await getLabActions()?.renameSampleFolder?.(id, name);
    } finally {
      setBusyId(null);
    }
  }

  async function onDelete(id: string): Promise<void> {
    setBusyId(id);
    try {
      await getLabActions()?.deleteSample?.(id);
      const clip = getSampleClipboard();
      if (clip?.kind === "sample" && clip.id === id) {
        clearSampleClipboard();
        setClipboardEpoch(n => n + 1);
      }
    } finally {
      setBusyId(null);
    }
  }

  function bumpClipboard(): void {
    setClipboardEpoch(n => n + 1);
  }

  function openContext(
    event: MouseEvent,
    target: SampleLibraryContextTarget,
  ): void {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      target,
      point: { x: event.clientX, y: event.clientY },
    });
  }

  /** Paste target folder: folder under cursor, sample’s parent, or Library root. */
  function pasteTargetFolderId(target: SampleLibraryContextTarget): string | null {
    if (target.kind === "folder") {
      return target.id;
    }
    if (target.kind === "sample") {
      return assets.find(a => a.id === target.id)?.folderId ?? null;
    }
    return null;
  }

  async function duplicateFolderTree(
    folderId: string,
    parentId: string | null,
  ): Promise<void> {
    const folder = folders.find(f => f.id === folderId);
    if (!folder) {
      return;
    }
    const actions = getLabActions();
    if (!actions) {
      return;
    }
    const createdId = await actions.createSampleFolder?.(folder.name, parentId);
    if (!createdId) {
      return;
    }
    const childSamples = assets.filter(a => (a.folderId ?? null) === folderId);
    for (const sample of childSamples) {
      await actions.duplicateSample?.(sample.id, createdId);
    }
    const childFolders = folders.filter(f => (f.parentId ?? null) === folderId);
    for (const child of childFolders) {
      await duplicateFolderTree(child.id, createdId);
    }
  }

  async function runPaste(target: SampleLibraryContextTarget): Promise<void> {
    const clip = getSampleClipboard();
    if (!clip) {
      return;
    }
    const destFolderId = pasteTargetFolderId(target);
    const actions = getLabActions();
    if (!actions) {
      return;
    }

    if (clip.kind === "sample") {
      if (clip.mode === "cut") {
        await actions.moveSampleToFolder?.(clip.id, destFolderId);
        clearSampleClipboard();
        bumpClipboard();
      } else {
        await actions.duplicateSample?.(clip.id, destFolderId);
      }
      reload();
      return;
    }

    // Folder
    if (clip.id === destFolderId) {
      getLabActions()?.setStatus?.("Cannot paste a folder into itself");
      return;
    }
    if (
      destFolderId != null &&
      isFolderAncestor(destFolderId, clip.id)
    ) {
      getLabActions()?.setStatus?.("Cannot paste a folder into its own subfolder");
      return;
    }
    if (clip.mode === "cut") {
      await actions.moveSampleFolder?.(clip.id, destFolderId);
      clearSampleClipboard();
      bumpClipboard();
    } else {
      await duplicateFolderTree(clip.id, destFolderId);
    }
    reload();
  }

  async function onContextAction(action: SampleLibraryMenuAction): Promise<void> {
    if (!contextMenu) {
      return;
    }
    const { target } = contextMenu;
    setContextMenu(null);

    if (action === "paste") {
      await runPaste(target);
      return;
    }

    if (target.kind === "root") {
      return;
    }

    if (target.kind === "sample") {
      const asset = assets.find(a => a.id === target.id);
      if (!asset) {
        return;
      }
      if (action === "cut") {
        setSampleClipboard({ kind: "sample", mode: "cut", id: target.id });
        bumpClipboard();
        getLabActions()?.setStatus?.(`Cut “${asset.name}”`);
        return;
      }
      if (action === "copy") {
        setSampleClipboard({ kind: "sample", mode: "copy", id: target.id });
        bumpClipboard();
        getLabActions()?.setStatus?.(`Copied “${asset.name}”`);
        return;
      }
      if (action === "delete") {
        await onDelete(target.id);
        return;
      }
      if (action === "export") {
        await getLabActions()?.exportSampleWav?.(target.id);
        return;
      }
      if (action === "rename") {
        setEditingId(target.id);
        setEditingFolderId(null);
        setDraftName(asset.name);
        return;
      }
      return;
    }

    // Folder
    const folder = folders.find(f => f.id === target.id);
    if (!folder) {
      return;
    }
    if (action === "cut") {
      setSampleClipboard({ kind: "folder", mode: "cut", id: target.id });
      bumpClipboard();
      getLabActions()?.setStatus?.(`Cut folder “${folder.name}”`);
      return;
    }
    if (action === "copy") {
      setSampleClipboard({ kind: "folder", mode: "copy", id: target.id });
      bumpClipboard();
      getLabActions()?.setStatus?.(`Copied folder “${folder.name}”`);
      return;
    }
    if (action === "delete") {
      setDeleteFolderConfirmId(target.id);
      return;
    }
    if (action === "rename") {
      setEditingFolderId(target.id);
      setEditingId(null);
      setDraftName(folder.name);
      setSelectedFolderId(target.id);
    }
  }

  function countSamplesInFolder(id: string): number {
    return assets.filter(a => {
      let cursor = normalizeParent(a.folderId);
      const seen = new Set<string>();
      while (cursor && !seen.has(cursor)) {
        if (cursor === id) {
          return true;
        }
        seen.add(cursor);
        cursor = normalizeParent(folderById.get(cursor)?.parentId);
      }
      return false;
    }).length;
  }

  async function onConfirmDeleteFolder(): Promise<void> {
    const id = deleteFolderConfirmId;
    if (!id || busyId === id) {
      return;
    }
    setDeleteFolderConfirmId(null);
    setBusyId(id);
    try {
      await getLabActions()?.deleteSampleFolder?.(id);
      if (selectedFolderId === id) {
        setSelectedFolderId(null);
      }
      const clip = getSampleClipboard();
      if (!clip) {
        return;
      }
      const inDeletedSubtree =
        clip.id === id ||
        (clip.kind === "folder" && isFolderAncestor(clip.id, id)) ||
        (clip.kind === "sample" &&
          (() => {
            const parent = assets.find(a => a.id === clip.id)?.folderId;
            return parent != null && (parent === id || isFolderAncestor(parent, id));
          })());
      if (inDeletedSubtree) {
        clearSampleClipboard();
        bumpClipboard();
      }
    } finally {
      setBusyId(null);
    }
  }

  async function importFiles(files: File[], folderId: string | null = selectedFolderId): Promise<void> {
    if (files.length === 0 || importBusy) {
      return;
    }
    setImportBusy(true);
    try {
      await getLabActions()?.importLibrarySamples?.(files, folderId);
      reload();
    } finally {
      setImportBusy(false);
    }
  }

  async function onExportLibrary(): Promise<void> {
    if (libraryBusy) {
      return;
    }
    setLibraryBusy(true);
    try {
      await getLabActions()?.exportSampleLibrary?.();
    } finally {
      setLibraryBusy(false);
    }
  }

  async function onImportLibrary(): Promise<void> {
    if (libraryBusy) {
      return;
    }
    setLibraryBusy(true);
    try {
      await getLabActions()?.importSampleLibrary?.();
      reload();
    } finally {
      setLibraryBusy(false);
    }
  }

  async function onConfirmWipe(): Promise<void> {
    if (libraryBusy) {
      return;
    }
    setWipeConfirmOpen(false);
    setLibraryBusy(true);
    try {
      await getLabActions()?.wipeSampleLibrary?.();
      setSelectedFolderId(null);
      setCollapsed(new Set());
      reload();
    } finally {
      setLibraryBusy(false);
    }
  }

  async function onNewFolder(): Promise<void> {
    await getLabActions()?.createSampleFolder?.("New Folder", selectedFolderId);
    if (selectedFolderId) {
      setCollapsed(prev => {
        const next = new Set(prev);
        next.delete(selectedFolderId);
        return next;
      });
    }
  }

  function toggleCollapsed(folderId: string): void {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }

  function acceptLibraryDrop(
    event: DragEvent,
    targetFolderId: string | null,
  ): boolean {
    if (isLibrarySampleDrag(event) || isLibraryFolderDrag(event)) {
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = "move";
      setDropTarget(targetFolderId ?? "root");
      return true;
    }
    return false;
  }

  async function handleLibraryDrop(
    event: DragEvent,
    targetFolderId: string | null,
  ): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    setDropTarget(null);

    if (isAudioFileDrag(event)) {
      await importFiles(audioFilesFromDrop(event), targetFolderId);
      return;
    }

    const sampleId = sampleIdFromDrop(event);
    if (sampleId) {
      const asset = assets.find(a => a.id === sampleId);
      if ((asset?.folderId ?? null) === targetFolderId) {
        return;
      }
      await getLabActions()?.moveSampleToFolder?.(sampleId, targetFolderId);
      return;
    }

    const folderId = folderIdFromDrop(event);
    if (folderId) {
      if (folderId === targetFolderId) {
        return;
      }
      // Prevent dropping onto self or descendants — API also guards.
      if (targetFolderId) {
        let cursor: string | null = targetFolderId;
        while (cursor) {
          if (cursor === folderId) {
            return;
          }
          cursor = normalizeParent(folderById.get(cursor)?.parentId);
        }
      }
      await getLabActions()?.moveSampleFolder?.(folderId, targetFolderId);
    }
  }

  function renderSampleRow(asset: SampleAssetMeta, depth: number): ReactNode {
    const used = usage.get(asset.id);
    const isUsed = Boolean(used && (used.tracks.length > 0 || used.bankNames.length > 0));
    const usageHint = used
      ? [
          ...used.tracks.map(t => `I${t + 1}`),
          ...used.bankNames.map(n => `preset “${n}”`),
        ].join(", ")
      : "";
    const editing = editingId === asset.id;
    const isPlaying = playingId === asset.id;
    const cutDim = clipboardEpoch >= 0 && sampleClipboardIsCut(asset.id);
    return (
      <li
        key={asset.id}
        draggable={!editing}
        className={[
          "group flex items-center gap-1.5 rounded px-1 py-1",
          isUsed ? "bg-[color-mix(in_srgb,var(--accent)_10%,transparent)]" : "hover:bg-white/[0.04]",
          cutDim ? "opacity-45" : "",
        ].join(" ")}
        style={{ paddingLeft: `${0.25 + depth * 0.75}rem` }}
        onContextMenu={event => openContext(event, { kind: "sample", id: asset.id })}
        onDragStart={event => {
          if (editing) {
            event.preventDefault();
            return;
          }
          setSampleDragData(event.dataTransfer, asset.id, asset.name);
          stopAudition();
        }}
      >
        <button
          type="button"
          className={[
            "inline-flex size-8 shrink-0 items-center justify-center rounded border border-transparent",
            isPlaying
              ? "bg-[color-mix(in_srgb,var(--accent)_28%,transparent)] text-white"
              : "text-slate-200 hover:border-lab-line hover:bg-white/8 hover:text-white",
          ].join(" ")}
          title={isPlaying ? "Stop audition" : "Play sample (oneshot)"}
          aria-label={isPlaying ? `Stop ${asset.name}` : `Play ${asset.name}`}
          aria-pressed={isPlaying}
          disabled={auditionBusy && !isPlaying}
          onClick={() => void toggleAudition(asset)}
        >
          {isPlaying ? (
            <Square size={16} className="fill-current" strokeWidth={2.25} aria-hidden />
          ) : (
            <Play size={16} className="fill-current" strokeWidth={2.25} aria-hidden />
          )}
        </button>
        <span
          className={[
            "flex size-6 shrink-0 items-center justify-center rounded",
            isUsed ? "text-lab-accent" : "text-slate-500",
          ].join(" ")}
          title={isUsed ? `Used by ${usageHint}` : "Not assigned"}
          aria-hidden={true}
        >
          {isUsed ? (
            <svg viewBox="0 0 24 24" width={14} height={14}>
              <rect x="3" y="7" width="18" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
              <path d="M7 19V9M10 19v-4M13 19V11M17 19V9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width={14} height={14}>
              <path d="M4 14v2M8 10v6M12 7v9M16 11v5M20 9v7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          )}
        </span>

        <div className="min-w-0 flex-1">
          {editing ? (
            <input
              autoFocus
              className="box-border w-full rounded border border-lab-line bg-lab-panel-deep px-1 py-0.5 text-[12px] text-slate-100 [font:inherit]"
              value={draftName}
              aria-label="Sample name"
              disabled={busyId === asset.id}
              onChange={event => setDraftName(event.target.value)}
              onBlur={() => void commitRename(asset.id)}
              onKeyDown={event => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void commitRename(asset.id);
                } else if (event.key === "Escape") {
                  event.preventDefault();
                  setEditingId(null);
                }
              }}
            />
          ) : (
            <button
              type="button"
              className="block w-full truncate border-none bg-transparent p-0 text-left text-[12px] text-slate-100 [font:inherit] hover:underline"
              title="Click to rename"
              onClick={() => {
                setEditingId(asset.id);
                setEditingFolderId(null);
                setDraftName(asset.name);
              }}
            >
              {asset.name}
            </button>
          )}
          <div className="truncate text-[10px] text-slate-500">
            {searching ? `${folderPathLabel(asset.folderId)} · ` : ""}
            {asset.source ?? "import"}
            {isUsed ? ` · ${usageHint}` : ""}
            {" · "}
            {(asset.lengthFrames / asset.sampleRate).toFixed(2)}s
          </div>
        </div>

        <button
          type="button"
          className="inline-flex size-9 shrink-0 items-center justify-center rounded border border-transparent text-rose-300/90 opacity-0 transition-opacity group-hover:opacity-100 hover:border-lab-line hover:bg-white/8 hover:text-rose-200 focus-visible:opacity-100 disabled:opacity-0 group-hover:disabled:opacity-40"
          title="Delete sample (⌘Z to undo)"
          aria-label={`Delete ${asset.name}`}
          disabled={busyId === asset.id}
          onClick={() => void onDelete(asset.id)}
        >
          <Trash2 size={20} strokeWidth={2.25} aria-hidden />
        </button>
      </li>
    );
  }

  function renderFolderNode(folder: SampleFolderMeta, depth: number): ReactNode {
    if (matchingFolderIds && !matchingFolderIds.has(folder.id)) {
      return null;
    }
    const isCollapsed = !searching && collapsed.has(folder.id);
    const selected = selectedFolderId === folder.id;
    const isDrop = dropTarget === folder.id;
    const editing = editingFolderId === folder.id;
    const childFolders = folders
      .filter(f => normalizeParent(f.parentId) === folder.id)
      .sort(compareSortThenName);
    const childSamples = (searching ? matchingAssets : assets)
      .filter(a => normalizeParent(a.folderId) === folder.id)
      .sort(compareSortThenName);
    const cutDim = clipboardEpoch >= 0 && sampleClipboardIsCut(folder.id);

    return (
      <li key={folder.id} className="m-0 list-none p-0">
        <div
          draggable={!editing}
          className={[
            "group flex items-center gap-1 rounded px-1 py-1",
            selected ? "bg-white/[0.06]" : "hover:bg-white/[0.04]",
            isDrop ? "outline outline-1 outline-lab-accent" : "",
            cutDim ? "opacity-45" : "",
          ].join(" ")}
          style={{ paddingLeft: `${0.25 + depth * 0.75}rem` }}
          onClick={() => setSelectedFolderId(folder.id)}
          onContextMenu={event => openContext(event, { kind: "folder", id: folder.id })}
          onDragStart={event => {
            if (editing) {
              event.preventDefault();
              return;
            }
            setFolderDragData(event.dataTransfer, folder.id, folder.name);
            stopAudition();
          }}
          onDragOver={event => {
            if (isAudioFileDrag(event)) {
              event.preventDefault();
              event.stopPropagation();
              event.dataTransfer.dropEffect = "copy";
              setDropTarget(folder.id);
              return;
            }
            acceptLibraryDrop(event, folder.id);
          }}
          onDragLeave={event => {
            if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
              return;
            }
            setDropTarget(prev => (prev === folder.id ? null : prev));
          }}
          onDrop={event => {
            void handleLibraryDrop(event, folder.id);
          }}
        >
          <button
            type="button"
            className="inline-flex size-8 shrink-0 items-center justify-center rounded text-slate-300 hover:bg-white/8 hover:text-slate-100"
            aria-label={isCollapsed ? "Expand folder" : "Collapse folder"}
            onClick={event => {
              event.stopPropagation();
              toggleCollapsed(folder.id);
            }}
          >
            {isCollapsed ? <ChevronRight size={16} strokeWidth={2.25} /> : <ChevronDown size={16} strokeWidth={2.25} />}
          </button>
          <Folder size={16} strokeWidth={2.25} className="shrink-0 text-amber-200/80" aria-hidden />
          <div className="min-w-0 flex-1">
            {editing ? (
              <input
                autoFocus
                className="box-border w-full rounded border border-lab-line bg-lab-panel-deep px-1 py-0.5 text-[12px] text-slate-100 [font:inherit]"
                value={draftName}
                aria-label="Folder name"
                disabled={busyId === folder.id}
                onClick={event => event.stopPropagation()}
                onChange={event => setDraftName(event.target.value)}
                onBlur={() => void commitFolderRename(folder.id)}
                onKeyDown={event => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void commitFolderRename(folder.id);
                  } else if (event.key === "Escape") {
                    event.preventDefault();
                    setEditingFolderId(null);
                  }
                }}
              />
            ) : (
              <button
                type="button"
                className="block w-full truncate border-none bg-transparent p-0 text-left text-[12px] font-medium text-slate-100 [font:inherit] hover:underline"
                title="Click to rename"
                onClick={event => {
                  event.stopPropagation();
                  setSelectedFolderId(folder.id);
                  setEditingFolderId(folder.id);
                  setEditingId(null);
                  setDraftName(folder.name);
                }}
              >
                {folder.name}
              </button>
            )}
          </div>
          <button
            type="button"
            className="inline-flex size-9 shrink-0 items-center justify-center rounded border border-transparent text-rose-300/90 opacity-0 transition-opacity group-hover:opacity-100 hover:border-lab-line hover:bg-white/8 hover:text-rose-200 focus-visible:opacity-100 disabled:opacity-0 group-hover:disabled:opacity-40"
            title="Delete folder and contents (⌘Z to undo)"
            aria-label={`Delete folder ${folder.name}`}
            disabled={busyId === folder.id}
            onClick={event => {
              event.stopPropagation();
              setDeleteFolderConfirmId(folder.id);
            }}
          >
            <Trash2 size={20} strokeWidth={2.25} aria-hidden />
          </button>
        </div>
        {!isCollapsed && (
          <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
            {childFolders.map(child => renderFolderNode(child, depth + 1))}
            {childSamples.map(asset => renderSampleRow(asset, depth + 1))}
          </ul>
        )}
      </li>
    );
  }

  if (!open) {
    return null;
  }

  const rootFolders = folders
    .filter(f => normalizeParent(f.parentId) == null)
    .sort(compareSortThenName);
  const rootSamples = (searching ? matchingAssets : assets)
    .filter(a => normalizeParent(a.folderId) == null)
    .sort(compareSortThenName);
  const empty = assets.length === 0 && folders.length === 0;
  const noMatches = searching && matchingAssets.length === 0 && (matchingFolderIds?.size ?? 0) === 0;
  const deleteFolderLabel = deleteFolderConfirmId
    ? (folders.find(f => f.id === deleteFolderConfirmId)?.name ?? "folder")
    : null;
  const deleteFolderSampleCount = deleteFolderConfirmId
    ? countSamplesInFolder(deleteFolderConfirmId)
    : 0;
  const deleteFolderBusy = deleteFolderConfirmId != null && busyId === deleteFolderConfirmId;

  return (
    <div
      ref={panelRef}
      className={[
        `samples-panel relative ${dockPanel}`,
        fileDragOver ? "ring-2 ring-inset ring-lab-accent/50" : "",
      ].join(" ")}
      tabIndex={0}
      role="region"
      aria-label="Samples"
      onDragOver={event => {
        if (!isAudioFileDrag(event)) {
          return;
        }
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
        setFileDragOver(true);
      }}
      onDragLeave={event => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
          return;
        }
        setFileDragOver(false);
      }}
      onDrop={event => {
        if (!isAudioFileDrag(event)) {
          return;
        }
        event.preventDefault();
        setFileDragOver(false);
        void importFiles(audioFilesFromDrop(event), selectedFolderId);
      }}
    >
      <div className={`samples-panel-header ${dockPanelHeader}`}>
        <div className={`samples-panel-title ${dockPanelTitle}`}>Samples</div>
        <button type="button" className={`samples-panel-close ${dockPanelClose}`} onClick={close}>
          Close
        </button>
      </div>

      <div className="samples-panel-actions flex shrink-0 flex-col gap-1.5 border-b border-lab-line px-[0.55rem] py-[0.4rem]">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            className="shrink-0 rounded border border-lab-line bg-lab-panel-deep px-2 py-[0.3rem] text-[11px] text-slate-200 hover:bg-white/[0.06] disabled:opacity-40"
            disabled={importBusy}
            onClick={() => fileRef.current?.click()}
          >
            {importBusy ? "Importing…" : "Import…"}
          </button>
          <button
            type="button"
            className="inline-flex shrink-0 items-center gap-1 rounded border border-lab-line bg-lab-panel-deep px-2 py-[0.3rem] text-[11px] text-slate-200 hover:bg-white/[0.06]"
            title={
              selectedFolderId
                ? `New folder inside “${folderById.get(selectedFolderId)?.name ?? "folder"}”`
                : "New folder in Library"
            }
            onClick={() => void onNewFolder()}
          >
            <FolderPlus size={14} strokeWidth={2.25} aria-hidden />
            Folder
          </button>
          <button
            type="button"
            className="shrink-0 rounded border border-lab-line bg-lab-panel-deep px-2 py-[0.3rem] text-[11px] text-slate-200 hover:bg-white/[0.06] disabled:opacity-40"
            disabled={libraryBusy}
            title="Export full sample library as .adamsamples"
            onClick={() => void onExportLibrary()}
          >
            Export lib
          </button>
          <button
            type="button"
            className="shrink-0 rounded border border-lab-line bg-lab-panel-deep px-2 py-[0.3rem] text-[11px] text-slate-200 hover:bg-white/[0.06] disabled:opacity-40"
            disabled={libraryBusy}
            title="Merge a .adamsamples library into this one"
            onClick={() => void onImportLibrary()}
          >
            Import lib
          </button>
          <button
            type="button"
            className="shrink-0 rounded border border-lab-line bg-lab-panel-deep px-2 py-[0.3rem] text-[11px] text-slate-200 hover:bg-white/[0.06] disabled:opacity-40"
            disabled={libraryBusy || empty}
            title="Permanently delete all samples and folders"
            onClick={() => setWipeConfirmOpen(true)}
          >
            Wipe…
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".wav,audio/wav,audio/x-wav,audio/*,.mp3,.ogg,.flac,.aif,.aiff,.m4a"
            multiple
            className="hidden"
            onChange={event => {
              const list = event.target.files ? Array.from(event.target.files) : [];
              event.target.value = "";
              void importFiles(list);
            }}
          />
        </div>
        <input
          type="search"
          className="box-border w-full rounded border border-lab-line bg-lab-panel-deep px-1.5 py-[0.3rem] text-[12px] text-slate-200 [font:inherit]"
          placeholder="Search samples…"
          value={query}
          aria-label="Search samples"
          onChange={event => setQuery(event.target.value)}
        />
      </div>

      <div className={dockPanelScroll}>
        {empty ? (
          <p className="muted m-0 px-1 py-2 text-[11px]">
            No samples yet — Import… or drop .wav files here. Use Folder to organize.
          </p>
        ) : noMatches ? (
          <p className="muted m-0 px-1 py-2 text-[11px]">No samples match this search.</p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
            <li className="m-0 list-none p-0">
              <div
                className={[
                  "flex items-center gap-1.5 rounded px-1 py-1 text-[11px] text-slate-400",
                  selectedFolderId == null ? "bg-white/[0.06] text-slate-200" : "hover:bg-white/[0.04]",
                  dropTarget === "root" ? "outline outline-1 outline-lab-accent" : "",
                ].join(" ")}
                onClick={() => setSelectedFolderId(null)}
                onContextMenu={event => openContext(event, { kind: "root" })}
                onDragOver={event => {
                  if (isAudioFileDrag(event)) {
                    event.preventDefault();
                    event.stopPropagation();
                    event.dataTransfer.dropEffect = "copy";
                    setDropTarget("root");
                    return;
                  }
                  acceptLibraryDrop(event, null);
                }}
                onDragLeave={event => {
                  if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
                    return;
                  }
                  setDropTarget(prev => (prev === "root" ? null : prev));
                }}
                onDrop={event => {
                  void handleLibraryDrop(event, null);
                }}
              >
                <Folder size={16} strokeWidth={2.25} className="text-slate-500" aria-hidden />
                <span className="font-medium text-slate-200">Library</span>
                <span className="truncate text-[10px] text-slate-500">
                  drop samples / folders here
                </span>
              </div>
              <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                {rootFolders.map(folder => renderFolderNode(folder, 1))}
                {rootSamples.map(asset => renderSampleRow(asset, 1))}
              </ul>
            </li>
          </ul>
        )}
      </div>

      {wipeConfirmOpen ? (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center bg-[rgba(8,10,14,0.72)] px-3 backdrop-blur-[1px]"
          role="presentation"
          onClick={() => {
            if (!libraryBusy) {
              setWipeConfirmOpen(false);
            }
          }}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="wipe-library-title"
            aria-describedby="wipe-library-desc"
            className="w-[min(280px,100%)] rounded-lg border border-lab-line bg-lab-panel px-3.5 py-3 shadow-[0_18px_40px_rgba(0,0,0,0.55)]"
            onClick={event => event.stopPropagation()}
            onKeyDown={event => {
              if (event.key === "Escape" && !libraryBusy) {
                setWipeConfirmOpen(false);
              }
            }}
          >
            <div id="wipe-library-title" className="text-[13px] font-semibold text-slate-100">
              Wipe sample library?
            </div>
            <p id="wipe-library-desc" className="mt-1.5 mb-0 text-[11px] leading-snug text-slate-400">
              Deletes all {assets.length} sample{assets.length === 1 ? "" : "s"} and{" "}
              {folders.length} folder{folders.length === 1 ? "" : "s"} from this library.
              This cannot be undone.
            </p>
            <div className="mt-3 flex justify-end gap-1.5">
              <button
                type="button"
                className="rounded border border-lab-line bg-lab-panel-deep px-2.5 py-1 text-[11px] text-slate-200 hover:bg-white/[0.06] disabled:opacity-40"
                disabled={libraryBusy}
                onClick={() => setWipeConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded border border-rose-500/45 bg-rose-500/15 px-2.5 py-1 text-[11px] text-rose-200 hover:bg-rose-500/25 disabled:opacity-40"
                disabled={libraryBusy}
                autoFocus
                onClick={() => void onConfirmWipe()}
              >
                {libraryBusy ? "Wiping…" : "Wipe library"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteFolderConfirmId && deleteFolderLabel ? (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center bg-[rgba(8,10,14,0.72)] px-3 backdrop-blur-[1px]"
          role="presentation"
          onClick={() => {
            if (!deleteFolderBusy) {
              setDeleteFolderConfirmId(null);
            }
          }}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-folder-title"
            aria-describedby="delete-folder-desc"
            className="w-[min(280px,100%)] rounded-lg border border-lab-line bg-lab-panel px-3.5 py-3 shadow-[0_18px_40px_rgba(0,0,0,0.55)]"
            onClick={event => event.stopPropagation()}
            onKeyDown={event => {
              if (event.key === "Escape" && !deleteFolderBusy) {
                setDeleteFolderConfirmId(null);
              }
            }}
          >
            <div id="delete-folder-title" className="text-[13px] font-semibold text-slate-100">
              Delete “{deleteFolderLabel}”?
            </div>
            <p id="delete-folder-desc" className="mt-1.5 mb-0 text-[11px] leading-snug text-slate-400">
              {deleteFolderSampleCount > 0
                ? `Removes this folder and ${deleteFolderSampleCount} sample${deleteFolderSampleCount === 1 ? "" : "s"} inside. ⌘Z undoes.`
                : "Removes this empty folder. ⌘Z undoes."}
            </p>
            <div className="mt-3 flex justify-end gap-1.5">
              <button
                type="button"
                className="rounded border border-lab-line bg-lab-panel-deep px-2.5 py-1 text-[11px] text-slate-200 hover:bg-white/[0.06] disabled:opacity-40"
                disabled={deleteFolderBusy}
                onClick={() => setDeleteFolderConfirmId(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded border border-rose-500/45 bg-rose-500/15 px-2.5 py-1 text-[11px] text-rose-200 hover:bg-rose-500/25 disabled:opacity-40"
                disabled={deleteFolderBusy}
                autoFocus
                onClick={() => void onConfirmDeleteFolder()}
              >
                {deleteFolderBusy ? "Deleting…" : "Delete folder"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {contextMenu ? (
        <SampleLibraryContextMenu
          target={contextMenu.target}
          point={contextMenu.point}
          canPaste={clipboardEpoch >= 0 && hasSampleClipboard()}
          onAction={action => void onContextAction(action)}
          onClose={() => setContextMenu(null)}
        />
      ) : null}
    </div>
  );
}
